// gallery.js — the bonsai save/load gallery subsystem.
//
// Owns the saved-tree collection and everything around presenting it: saving
// the current tree (with a rendered thumbnail), the modal UI, load/delete,
// and toast notifications. Tree (re)creation itself stays in core/main — this
// module calls app.createNewTreeWithData() rather than constructing a Tree, so
// it never needs to import the Tree class or touch the scene graph directly.
//
// Persistence: savedTrees is mirrored to localStorage (STORAGE_KEY) on every
// mutation via persist(), and restored on startup via loadFromStorage(), so the
// gallery survives reloads. Each saved tree stores a seed + edit history so Load
// reproduces it exactly (see main.js createNewTreeWithData / Tree.replayHistory).

import * as THREE from 'three';
import { parameters, app } from './core.js';
import { syncControlsFromParameters } from './ui-controls.js';
import { playSfx } from './audio.js';

// Gallery-only state.
let savedTrees = [];
let isGalleryOpen = false;

// localStorage key for the persisted gallery (bump the suffix if the shape of
// the saved data ever changes incompatibly).
const STORAGE_KEY = 'bonsai.gallery.v1';

// Serialize the live `parameters` object to plain JSON. Everything is already a
// number/string except windDirection (a THREE.Vector3), which we flatten.
function serializeParameters() {
  return {
    ...parameters,
    windDirection: {
      x: parameters.windDirection.x,
      y: parameters.windDirection.y,
      z: parameters.windDirection.z
    }
  };
}

// Inverse of serializeParameters: rehydrate windDirection back into a Vector3 so
// downstream code (which calls Vector3 methods on it) keeps working.
function deserializeParameters(saved) {
  const out = { ...saved };
  if (saved && saved.windDirection && !saved.windDirection.isVector3) {
    const w = saved.windDirection;
    out.windDirection = new THREE.Vector3(w.x, w.y, w.z);
  }
  return out;
}

// Persist the whole gallery to localStorage. Thumbnails (base64 PNGs) dominate
// the payload, so if we blow the ~5MB quota we retry once without them rather
// than losing the save entirely.
function persist() {
  const write = (trees) =>
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...exportGallery(), trees }));
  try {
    write(savedTrees);
  } catch (err) {
    if (err && (err.name === 'QuotaExceededError' || err.code === 22)) {
      try {
        write(savedTrees.map(({ thumbnail, ...rest }) => rest));
        showStorageFullNotification();
      } catch (err2) {
        console.warn('Gallery could not be saved to localStorage:', err2);
      }
    } else {
      console.warn('Gallery could not be saved to localStorage:', err);
    }
  }
}

// Load any persisted gallery on startup. Tolerates missing/corrupt data.
function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw);
    if (data && Array.isArray(data.trees)) {
      savedTrees = data.trees;
    }
  } catch (err) {
    console.warn('Could not read saved gallery from localStorage:', err);
  }
}

// ===========================================
// TREE NAME (the #treeName field in #control-col)
// ===========================================

// Whimsical default names for freshly generated trees.
const TREE_NAMES = [
  'Zen Master', 'Ancient Wisdom', 'Morning Dew', 'Silent Grove',
  'Peaceful Valley', 'Misty Mountain', 'Autumn Breeze', 'Golden Light',
  'Mystic Forest', 'Tranquil Pond', 'Sacred Branch', 'Moonlit Garden',
  'Whispering Leaves', 'Desert Bloom', 'Hidden Valley', 'Crystal Stream',
  'Eternal Spring', 'Bamboo Grove', 'Cherry Blossom', 'Pine Shadow',
  'Lotus Pond', 'Mountain Echo', 'River Stone', 'Wind Dancer', 'Elder Root',
  'Shadowleaf', 'Bonsai-san', 'Koke-chan', 'Sakura-chan', 'Hana-mizu',
  'Kaze-matsu', 'Ishi-zumi',
  // More poetic English nature/Zen phrases
  'Frost Hollow', 'Quiet Ember', 'Snowfall Pine', 'Twilight Cedar',
  'Drifting Cloud', 'Stone Lantern', 'Evening Mist', 'Old Soul',
  'Patient Stone', 'Gentle Slope', 'Hushed Meadow', 'First Snow',
  'Distant Thunder', 'Moss Garden', 'Weathered Oak', 'Quiet Tide',
  'Fading Light', 'Emerald Hush', 'Northern Wind', 'Hollow Reed',
  'Whispering Pine', 'Velvet Moss', 'Wandering Brook', 'Slow River',
  // More Japanese-flavored names
  'Yuki-matsu', 'Take-chan', 'Tsuki-mori', 'Aki-kaze',
  'Mori-no-ko', 'Kumo-chan', 'Take-zen', 'Nami-ko', 'Tora-matsu'
];

function randomTreeName() {
  return TREE_NAMES[Math.floor(Math.random() * TREE_NAMES.length)];
}

function getTreeNameField() {
  return document.getElementById('treeName');
}

// Current name from the field, trimmed; falls back to a random name if blank.
function currentTreeName() {
  const field = getTreeNameField();
  const v = field && field.value.trim();
  return v || randomTreeName();
}

function setTreeNameField(name) {
  const field = getTreeNameField();
  if (field) field.value = name;
}

// Called by main.js when a brand-new tree is generated: it's no longer tied to a
// saved entry, so clear the current id and give it a fresh default name. Save
// will then create a new gallery entry rather than overwriting a loaded one.
app.newTreeIdentity = function() {
  app.currentTreeId = null;
  setTreeNameField(randomTreeName());
};

// ===========================================
// CORE GALLERY FUNCTIONS
// ===========================================

// Capture the on-screen tree's full save payload (everything except identity
// fields id/name/savedAt and the thumbnail, which are managed by saveBonsai).
function captureTreeData() {
  return {
    // Full parameter set (not the old hand-picked subset) so look is restored
    // exactly. seed + history are the fidelity payload: the seed regenerates the
    // identical base tree and history replays the post-generation edits.
    parameters: serializeParameters(),
    seed: app.tree.seed,
    history: app.tree.history ? [...app.tree.history] : [],
    // Trunk dims the branches were sized against (frozen at generation). Lets
    // replay rebuild branches at the right size even if the trunk was reshaped
    // afterward — branches don't resize on reshape, on purpose.
    genTrunc: app.tree.genTrunc,
    // `branches` holds only the small trunk branches; the big "main" branches
    // live on the mesh flagged userData.isMainBranch, so count them separately.
    branchCount: app.tree.branches ? app.tree.branches.filter(b => !b.isPruned).length : 0,
    mainBranchCount: app.tree.mesh.children.filter(child => child.userData && child.userData.isMainBranch).length,
    hasMainBranches: app.tree.mesh.children.some(child => child.userData && child.userData.isMainBranch),
    leanData: {
      leanX: app.tree.leanX,
      leanZ: app.tree.leanZ,
      curveStrength: app.tree.curveStrength
    }
  };
}

/**
 * Save the current bonsai. If it was loaded from (or previously saved to) the
 * gallery, this UPDATES that same entry in place; otherwise it creates a new one.
 */
window.saveBonsai = function() {
  if (!app.tree) {
    alert('No tree to save! Generate a tree first.');
    return;
  }

  playSfx('sparkle'); // save confirmation chime

  const name = currentTreeName();
  setTreeNameField(name); // reflect the fallback name if the field was blank

  // Are we updating an existing saved tree (the one currently on screen)?
  const existing = app.currentTreeId != null
    ? savedTrees.find(t => t.id === app.currentTreeId)
    : null;

  const target = existing || { id: Date.now() };
  Object.assign(target, captureTreeData(), {
    name,
    savedAt: new Date().toLocaleString()
  });

  // (Re)generate the thumbnail onto the target, then commit.
  generateTreeThumbnail(target, () => {
    if (!existing) savedTrees.push(target);
    app.currentTreeId = target.id; // subsequent Saves update this same tree
    persist();
    updateGalleryCount();
    if (existing) {
      showUpdateNotification(name);
      console.log(`Updated bonsai: ${name}`);
    } else {
      showSaveNotification(name);
      console.log(`Saved bonsai: ${name}`);
    }
  });
};

/**
 * Generate thumbnail for saved tree
 */
function generateTreeThumbnail(treeData, callback) {
  // Create a smaller renderer for thumbnails
  const thumbSize = 256;
  const thumbRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  thumbRenderer.setSize(thumbSize, thumbSize);
  thumbRenderer.shadowMap.enabled = true;
  thumbRenderer.shadowMap.type = THREE.PCFSoftShadowMap;

  // Create thumbnail scene. No fog here: at this camera distance the sky-blue
  // FogExp2 blended ~a third of its color over the tree and washed out the
  // foliage/bark. Thumbnails are tight isolated shots, so we want full color.
  const thumbScene = new THREE.Scene();

  // Create thumbnail camera
  const thumbCamera = new THREE.PerspectiveCamera(60, 1, 0.1, 1000);
  thumbCamera.position.set(0, 80, 120);
  thumbCamera.lookAt(0, 40, 0);

  // Add simple lighting. Turned up from 0.6/0.8 so the tree reads brighter in the
  // thumbnail; the hemisphere light also gets a lighter ground tone so shadowed
  // undersides aren't so dark.
  const ambientLight = new THREE.HemisphereLight(0xffffff, 0x777777, 1.0);
  thumbScene.add(ambientLight);

  const directionalLight = new THREE.DirectionalLight(0xffffff, 1.3);
  directionalLight.position.set(80, 120, 60);
  directionalLight.castShadow = true;
  thumbScene.add(directionalLight);

  // Create simple ground. Paint the disc with a vertical gradient that mimics the
  // old fog's depth cue — a fresh green near the camera fading to a pale, misty
  // teal toward the back — so the receding "horizon" sets the tree apart. Unlike
  // the fog, this only touches the ground, so the tree stays fully colored.
  const groundCanvas = document.createElement('canvas');
  groundCanvas.width = groundCanvas.height = 256;
  const gctx = groundCanvas.getContext('2d');
  const groundGrad = gctx.createLinearGradient(0, 0, 0, 256);
  groundGrad.addColorStop(0, '#9af9e5'); // far edge — pale misty teal (pushes back)
  groundGrad.addColorStop(1, '#007928'); // near edge — fresh green
  gctx.fillStyle = groundGrad;
  gctx.fillRect(0, 0, 256, 256);
  const groundTexture = new THREE.CanvasTexture(groundCanvas);
  // Canvas pixels are sRGB; without this the map is read as linear and the
  // renderer darkens it into a muddy "muted green" instead of the real colors.
  groundTexture.colorSpace = THREE.SRGBColorSpace;

  const groundGeo = new THREE.CircleGeometry(200, 32);
  const groundMat = new THREE.MeshStandardMaterial({ map: groundTexture });
  const ground = new THREE.Mesh(groundGeo, groundMat);
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = -30;
  thumbScene.add(ground);

  // Borrow the live tree mesh for the off-screen render. THREE.add() reparents,
  // which yanks it out of the main scene — so remember where it was and put it
  // back afterward, otherwise the tree vanishes from view after every Save.
  const treeMesh = app.tree.mesh;
  const originalParent = treeMesh.parent;
  thumbScene.add(treeMesh);

  // Render thumbnail
  thumbRenderer.render(thumbScene, thumbCamera);

  // Convert to data URL. WebP keeps the transparent background but is ~60-80%
  // smaller than PNG, so far more thumbnails fit in the ~5MB localStorage budget.
  // toDataURL silently falls back to PNG on browsers that can't encode WebP, so
  // verify the prefix and pass through the PNG it gave us in that case.
  const canvas = thumbRenderer.domElement;
  const webp = canvas.toDataURL('image/webp', 0.8);
  treeData.thumbnail = webp.startsWith('data:image/webp')
    ? webp
    : canvas.toDataURL('image/png');

  // Return the tree to the main scene, then release the throwaway GL context
  // (each Save makes a new WebGLRenderer; browsers cap how many can be live).
  if (originalParent) originalParent.add(treeMesh);
  thumbRenderer.dispose();

  callback();
}

/**
 * Load saved tree by ID
 */
window.loadTree = function(treeId) {
  const treeData = savedTrees.find(t => t.id == treeId);
  if (!treeData) {
    alert('Tree not found!');
    return;
  }

  // Apply saved parameters (rehydrating windDirection back into a Vector3).
  Object.assign(parameters, deserializeParameters(treeData.parameters));

  // Update UI controls (every slider + select, so they match the loaded tree)
  syncControlsFromParameters();

  // Rebuild the tree from saved data (core removes the old tree + animates in)
  app.createNewTreeWithData(treeData);

  // Track which saved tree is now on screen so a later Save updates it in place,
  // and show its name in the control panel.
  app.currentTreeId = treeData.id;
  setTreeNameField(treeData.name);

  toggleGallery(); // Close gallery

  showLoadNotification(treeData.name);
};

// ===========================================
// SHARE HOOKS (consumed by shareBonsai.js)
// ===========================================
// gallery owns "tree data <-> scene"; shareBonsai.js owns "tree data <-> link".
// These small exports are the seam between them, so the share feature reuses the
// exact same capture/replay/notification machinery as Save and Load.

// The shareable payload: the same reproducible data Save stores (minus the
// thumbnail and gallery id), plus the on-screen name so the recipient sees it.
export function captureShareData() {
  return { ...captureTreeData(), name: currentTreeName() };
}

// Same payload, but for an already-saved gallery entry rather than the live
// tree — so the 🔗 on a card shares that card's stored config. We pick only the
// reproducible fields (createNewTreeWithData/replayHistory need seed, history,
// leanData; applySharedTree needs parameters + name); the parameters are already
// serialized (windDirection flattened) from save time. Returns null if missing.
export function captureShareDataFor(treeId) {
  const t = savedTrees.find(x => x.id == treeId);
  if (!t) return null;
  return {
    parameters: t.parameters,
    seed: t.seed,
    history: t.history || [],
    genTrunc: t.genTrunc,
    leanData: t.leanData,
    name: t.name
  };
}

// Rebuild a tree received via a share link. Mirrors loadTree, but the incoming
// tree isn't a gallery entry — it's unsaved until the recipient hits Save, so we
// clear currentTreeId (next Save makes a new entry) rather than tracking an id.
export function applySharedTree(payload) {
  Object.assign(parameters, deserializeParameters(payload.parameters));
  syncControlsFromParameters();
  app.createNewTreeWithData(payload);
  app.currentTreeId = null;
  const name = payload.name || randomTreeName();
  setTreeNameField(name);
  notify(`📩 Shared bonsai: ${name}`, 'linear-gradient(45deg, #16a085, #1abc9c)');
}

// Generic toast helper so shareBonsai can show notifications without duplicating
// the createNotification/showNotification DOM plumbing.
export function notify(message, background) {
  showNotification(createNotification(message, background));
}

/**
 * Delete saved tree by ID
 */
window.deleteTree = function(treeId) {
  const treeIndex = savedTrees.findIndex(t => t.id == treeId);
  if (treeIndex === -1) {
    alert('Tree not found!');
    return;
  }

  const treeName = savedTrees[treeIndex].name;
  if (confirm(`Are you sure you want to delete "${treeName}"?`)) {
    const deletedId = savedTrees[treeIndex].id;
    savedTrees.splice(treeIndex, 1);
    // If the on-screen tree was this one, it's now unsaved — next Save makes a
    // new entry rather than resurrecting the deleted id.
    if (app.currentTreeId === deletedId) app.currentTreeId = null;
    persist();
    renderGallery(); // Refresh gallery display
    showDeleteNotification(treeName);
  }
};

// ===========================================
// UI FUNCTIONS
// ===========================================

/**
 * Toggle gallery modal visibility
 */
window.toggleGallery = function() {
  const modal = document.getElementById('galleryModal');
  const controls = document.getElementById('control-col');
  isGalleryOpen = !isGalleryOpen;

  if (isGalleryOpen) {
    modal.style.display = 'flex';
    controls.style.display = 'none';
    renderGallery();
    // Animate in
    requestAnimationFrame(() => {
      modal.style.opacity = '0';
      modal.style.transform = 'scale(0.9)';
      modal.style.transition = 'all 0.3s ease';
      requestAnimationFrame(() => {
        modal.style.opacity = '1';
        modal.style.transform = 'scale(1)';
      });
    });
  } else {
    controls.style.display = 'flex';
    // Animate out
    modal.style.opacity = '0';
    modal.style.transform = 'scale(0.9)';
    setTimeout(() => {
      modal.style.display = 'none';
    }, 300);
  }
};

/**
 * Render gallery content
 */
function renderGallery() {
  const shelf = document.getElementById('galleryShelf');
  updateGalleryCount();

  if (savedTrees.length === 0) {
    shelf.innerHTML = `
      <div class="empty-gallery">
        <div class="empty-gallery-icon">🌳</div>
        <h3>Your Gallery is Empty</h3>
      </div>
      <p class="noTrees">Save bonsai trees to see them here!</p>
      <button class="btn" onclick="toggleGallery()">← Settings</button>
    `;
    return;
  }

  shelf.innerHTML = savedTrees.map(treeData => `
    <div class="tree-card" onclick="previewTree('${treeData.id}')">
    <div class="tree-name">${treeData.name}</div>
    <div class="tree-inner-card">
      <div class="tree-thumbnail">
        ${treeData.thumbnail ?
          `<img src="${treeData.thumbnail}" style="width:100%;height:100%;object-fit:cover;border-radius:8px;" alt="${treeData.name}">` :
          '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-size:48px;">🌳</div>'
        }
      </div>
      <div class="tree-info">
        <div class="tree-details">
         ${treeData.savedAt}<br>
          Branches: ${treeData.branchCount}${treeData.mainBranchCount ? ` + ${treeData.mainBranchCount} main` : ''}<br>
          Type: ${treeData.parameters.leafType}<br>
          Color: ${treeData.parameters.foliageColor}
        </div>
        <div class="tree-actions">
          <button class="tree-btn load" onclick="event.stopPropagation(); loadTree('${treeData.id}')">
            Load
          </button>
          <button class="tree-btn share" onclick="event.stopPropagation(); shareGalleryTree('${treeData.id}')" aria-label="Share ${treeData.name}" title="Share link">
            🔗
          </button>
          <button class="tree-btn delete" onclick="event.stopPropagation(); deleteTree('${treeData.id}')">
            Delete
          </button>
        </div>
      </div>
      </div>
    </div>
  `).join('');
}

/**
 * Preview tree (same as load for now)
 */
window.previewTree = function(treeId) {
  loadTree(treeId);
};

/**
 * Update gallery count display
 */
function updateGalleryCount() {
  const countElement = document.getElementById('treeCount');
  if (countElement) {
    countElement.textContent = savedTrees.length;
  }
}

// ===========================================
// NOTIFICATION SYSTEM
// ===========================================

/**
 * Show save success notification
 */
function showSaveNotification(treeName) {
  const notification = createNotification(
    `✅ Saved: ${treeName}`,
    'linear-gradient(45deg, #8e44ad, #9b59b6)'
  );
  showNotification(notification);
}

/**
 * Show update success notification (saved over an existing tree)
 */
function showUpdateNotification(treeName) {
  const notification = createNotification(
    `💾 Updated: ${treeName}`,
    'linear-gradient(45deg, #8e44ad, #9b59b6)'
  );
  showNotification(notification);
}

/**
 * Show load success notification
 */
function showLoadNotification(treeName) {
  const notification = createNotification(
    `📥 Loaded: ${treeName}`,
    'linear-gradient(45deg, #3498db, #2980b9)'
  );
  showNotification(notification);
}

/**
 * Show delete success notification
 */
function showDeleteNotification(treeName) {
  const notification = createNotification(
    `🗑️ Deleted: ${treeName}`,
    'linear-gradient(45deg, #e74c3c, #c0392b)'
  );
  showNotification(notification);
}

/**
 * Warn that the gallery was saved without thumbnails (localStorage was full)
 */
function showStorageFullNotification() {
  const notification = createNotification(
    '⚠️ Storage full — saved without thumbnails',
    'linear-gradient(45deg, #e67e22, #d35400)'
  );
  showNotification(notification);
}

/**
 * Create notification element
 */
function createNotification(message, background) {
  const notification = document.createElement('div');
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: ${background};
    color: white;
    padding: 15px 20px;
    border-radius: 10px;
    font-weight: bold;
    z-index: 3000;
    box-shadow: 0 4px 20px rgba(0,0,0,0.3);
    transform: translateX(100%);
    transition: transform 0.3s ease;
    max-width: 300px;
  `;
  notification.textContent = message;
  return notification;
}

/**
 * Show and animate notification
 */
function showNotification(notification) {
  document.body.appendChild(notification);

  // Animate in
  setTimeout(() => {
    notification.style.transform = 'translateX(0)';
  }, 100);

  // Remove after 3 seconds
  setTimeout(() => {
    notification.style.transform = 'translateX(100%)';
    setTimeout(() => {
      if (notification.parentNode) {
        document.body.removeChild(notification);
      }
    }, 300);
  }, 3000);
}

// ===========================================
// INITIALIZATION & EVENT HANDLERS
// ===========================================

/**
 * Initialize gallery system
 */
function initializeGallery() {
  // Restore any previously-saved trees from localStorage.
  loadFromStorage();

  // Close gallery when clicking outside
  const galleryModal = document.getElementById('galleryModal');
  if (galleryModal) {
    galleryModal.addEventListener('click', function(e) {
      if (e.target === this) {
        toggleGallery();
      }
    });
  }

  // Initialize gallery count
  updateGalleryCount();

  console.log('Gallery system initialized');
}

// ===========================================
// UTILITY FUNCTIONS
// ===========================================

/**
 * Export gallery data (for debugging or future persistence)
 */
function exportGallery() {
  return {
    version: '1.0',
    exportDate: new Date().toISOString(),
    trees: savedTrees
  };
}

/**
 * Import gallery data (for debugging or future persistence)
 */
function importGallery(galleryData) {
  if (galleryData && galleryData.trees && Array.isArray(galleryData.trees)) {
    savedTrees = galleryData.trees;
    updateGalleryCount();
    if (isGalleryOpen) {
      renderGallery();
    }
    console.log(`Imported ${savedTrees.length} trees`);
  }
}

/**
 * Get gallery statistics
 */
function getGalleryStats() {
  const stats = {
    totalTrees: savedTrees.length,
    leafTypes: {},
    foliageColors: {},
    averageBranches: 0,
    oldestTree: null,
    newestTree: null
  };

  if (savedTrees.length === 0) return stats;

  // Calculate statistics
  savedTrees.forEach(tree => {
    // Count leaf types
    const leafType = tree.parameters.leafType;
    stats.leafTypes[leafType] = (stats.leafTypes[leafType] || 0) + 1;

    // Count foliage colors
    const color = tree.parameters.foliageColor;
    stats.foliageColors[color] = (stats.foliageColors[color] || 0) + 1;

    // Sum branches for average
    stats.averageBranches += tree.branchCount;
  });

  // Calculate averages
  stats.averageBranches = Math.round(stats.averageBranches / savedTrees.length);

  // Find oldest and newest
  stats.oldestTree = savedTrees.reduce((oldest, tree) =>
    tree.id < oldest.id ? tree : oldest
  );

  stats.newestTree = savedTrees.reduce((newest, tree) =>
    tree.id > newest.id ? tree : newest
  );

  return stats;
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeGallery);
} else {
  initializeGallery();
}
